import { PropsWithChildren, useEffect, useRef } from "react";
import { Animated, Easing } from "react-native";

import { useReducedMotion } from "../hooks/useReducedMotion";
import { supportsNativeDriver } from "../lib/animation";
import { motion } from "../theme/tokens";

export function Reveal({
  children,
  delay = 0,
  direction = "up",
  distance = 18,
  stagger = 0,
  scale: enableScale = false,
}: PropsWithChildren<{
  delay?: number;
  direction?: "up" | "down" | "left" | "right";
  distance?: number;
  stagger?: number;
  scale?: boolean;
}>) {
  const { reduceMotion } = useReducedMotion();
  const opacity = useRef(new Animated.Value(reduceMotion ? 1 : 0)).current;
  const translation = useRef(new Animated.Value(reduceMotion ? 0 : distance)).current;
  const scaleVal = useRef(
    new Animated.Value(reduceMotion || !enableScale ? 1 : motion.scaleFrom),
  ).current;
  const totalDelay = delay + stagger;

  useEffect(() => {
    if (reduceMotion) {
      opacity.setValue(1);
      translation.setValue(0);
      scaleVal.setValue(1);
      return;
    }

    opacity.setValue(0);
    translation.setValue(distance);
    scaleVal.setValue(enableScale ? motion.scaleFrom : 1);

    const animations = [
      Animated.timing(opacity, {
        toValue: 1,
        duration: motion.entranceDuration,
        delay: totalDelay,
        easing: Easing.bezier(...motion.easing),
        useNativeDriver: supportsNativeDriver,
      }),
      Animated.timing(translation, {
        toValue: 0,
        duration: motion.entranceDuration,
        delay: totalDelay,
        easing: Easing.bezier(...motion.easing),
        useNativeDriver: supportsNativeDriver,
      }),
    ];

    if (enableScale) {
      animations.push(
        Animated.timing(scaleVal, {
          toValue: 1,
          duration: motion.entranceDuration,
          delay: totalDelay,
          easing: Easing.bezier(...motion.easing),
          useNativeDriver: supportsNativeDriver,
        }),
      );
    }

    const entrance = Animated.parallel(animations);
    entrance.start();
    return () => entrance.stop();
  }, [totalDelay, opacity, translation, scaleVal, enableScale, reduceMotion, distance]);

  const transform = [
    direction === "up" || direction === "down"
      ? { translateY: direction === "up" ? translation : Animated.multiply(translation, -1) }
      : { translateX: direction === "left" ? translation : Animated.multiply(translation, -1) },
  ];

  if (enableScale && !reduceMotion) {
    transform.push({ scale: scaleVal } as never);
  }

  return (
    <Animated.View style={[{ opacity }, reduceMotion ? null : { transform }]}>
      {children}
    </Animated.View>
  );
}
